import _ from 'lodash';

import { getTimestampsInRange, resolutionToSeconds } from 'src/lib/date/date';
import { Timestamp } from 'src/lib/graphqlTypes/customScalarTypes';
import { Resolution } from 'src/lib/graphqlTypes/types';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { ScalarTimeSeriesData, TimeSeriesData } from './dataTypes';

/**
 * Given a dataset and a time range, returns a zero-padded dataset
 * of ScalarTimeSeriesData points where the value is computed by getValue().
 */
export function zeroPadTimeSeriesData<TRecord extends TimeSeriesData, TValue>({
  data,
  timeFrom,
  timeTo,
  resolution,
  getValue,
  getZeroFillValue,
}: {
  data: TRecord[];
  timeFrom: Date;
  timeTo: Date;
  resolution: Resolution;
  getValue: (record: TRecord) => TValue;
  getZeroFillValue: (timestamp: Timestamp['output']) => TValue;
}): ScalarTimeSeriesData<TValue>[] {
  // First zip up the flat list into a timestamp-keyed map of records.
  // Clamp to the correct time boundaries using steps that
  // match our query resolution.
  const resInMs = resolutionToSeconds(resolution) * 1000;
  const recordsByTime = _.groupBy(data, (record) => {
    const truncatedTimestamp = new Date(
      resInMs * Math.floor(new Date(record.timestamp).valueOf() / resInMs),
    );
    return truncatedTimestamp.toISOString();
  });
  const normalizedTimeSeriesData = getTimestampsInRange(
    timeFrom,
    timeTo,
    resolution,
  ).map((tsKey) => {
    // Loop over all potential timestamps, then loop over all groups.
    // If we find a matching record for timestamp, use it,
    // otherwise use a zero-filler.

    const timestampedRecords = recordsByTime[tsKey];
    if (isMinLengthArray(2, timestampedRecords)) {
      throw new Error('expected exactly one record per timestamp');
    }
    return isMinLengthArray(1, timestampedRecords)
      ? { timestamp: tsKey, value: getValue(timestampedRecords[0]) }
      : { timestamp: tsKey, value: getZeroFillValue(tsKey) };
  });
  return normalizedTimeSeriesData;
}
